<template>
  <PageContainer>
    <View direction="column" gap="xs" class="page-header">
      <Text variant="h1">Integrations</Text>
      <Text variant="body-large" color="secondary">Connect Tallo with the tools you already use</Text>
    </View>

    <View v-if="isLoading" justify="center" padding="xl">
      <LoadingIndicator />
    </View>

    <View v-else direction="row" gap="md" align="start" class="layout">
      <Card size="medium" class="category-rail">
        <View direction="column" gap="xs" class="rail-items">
          <button
            v-for="category in categoryFilters"
            :key="category.id"
            type="button"
            class="rail-item"
            :class="{ active: selectedCategory === category.id }"
            @click="selectedCategory = category.id"
            :data-testid="`integrations-category-${category.id}`"
          >
            <Text variant="label-medium">{{ category.label }}</Text>
            <Text variant="body-medium" color="tertiary">{{ category.count }}</Text>
          </button>

          <Divider class="rail-divider" />

          <button
            type="button"
            class="rail-item"
            :class="{ active: selectedCategory === connectedFilterId }"
            @click="selectedCategory = connectedFilterId"
            data-testid="integrations-category-connected"
          >
            <Text variant="label-medium">Connected</Text>
            <Text variant="body-medium" color="tertiary">{{ connectedCount }}</Text>
          </button>
        </View>
      </Card>

      <View direction="column" gap="md" class="results">
        <Input v-model="searchQuery" :placeholder="searchPlaceholder" data-testid="integrations-search" />

        <div v-if="visibleIntegrations.length" class="card-grid" data-testid="integrations-grid">
          <Card
            v-for="integration in visibleIntegrations"
            :key="integration.id"
            shadow="on-hover"
            class="integration-card"
            :class="{ openable: isOpenable(integration) }"
            @click="openDetails(integration)"
            :data-testid="`integration-card-${integration.id}`"
          >
            <View direction="column" gap="sm">
              <View justify="space-between" align="start" gap="sm">
                <IntegrationLogo :tile="integration.tile" :alt="integration.name" />
                <View align="center" class="card-action">
                  <Button
                    v-if="statusOf(integration.id).connected || integration.supported"
                    variant="secondary"
                    size="small"
                    @click.stop="openDetails(integration)"
                  >
                    {{ statusOf(integration.id).connected ? 'Manage' : 'Connect' }}
                  </Button>
                  <Text v-else variant="body-small" color="tertiary">Coming soon</Text>
                </View>
              </View>

              <View direction="column" gap="xs">
                <Text variant="label-large">{{ integration.name }}</Text>
                <Text variant="body-medium" color="secondary">{{ integration.description }}</Text>
              </View>

              <View justify="space-between" align="center" gap="sm" class="card-footer">
                <template v-if="statusOf(integration.id).connected">
                  <Badge variant="green" label="Connected" size="small" />
                  <Text variant="body-small" color="tertiary" class="footer-meta">
                    {{ statusOf(integration.id).meta }}
                  </Text>
                </template>
              </View>
            </View>
          </Card>
        </div>

        <Hint v-else>No integrations match “{{ searchQuery }}”.</Hint>
      </View>
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import {
  Badge,
  Button,
  Card,
  Divider,
  Hint,
  Input,
  LoadingIndicator,
  PageContainer,
  Text,
  View,
} from '@tallo/design-system';
import { AppRouteNames } from '@tallo/investor/navigation';

import IntegrationLogo from './integration-logo.vue';
import { IntegrationCategory, IntegrationDefinition, integrations } from './integrations.config';
import { useIntegrationStatus } from './use-integration-status';

const router = useRouter();
const { statusOf, connectedCount, isLoading } = useIntegrationStatus();

const allFilterId = 'all';
const connectedFilterId = 'connected';

const selectedCategory = ref<string>(allFilterId);
const searchQuery = ref('');

const searchPlaceholder = computed(() => `Search ${integrations.length} integrations`);

const categoryFilters = computed(() => [
  { id: allFilterId, label: 'All integrations', count: integrations.length },
  ...Object.values(IntegrationCategory).map((category) => ({
    id: category,
    label: category,
    count: integrations.filter((integration) => integration.category === category).length,
  })),
]);

const visibleIntegrations = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  return integrations.filter((integration) => {
    const matchesCategory =
      selectedCategory.value === allFilterId ||
      (selectedCategory.value === connectedFilterId
        ? statusOf(integration.id).connected
        : integration.category === selectedCategory.value);

    return matchesCategory && (!query || integration.name.toLowerCase().includes(query));
  });
});

// Only integrations we actually support have somewhere to go.
function isOpenable(integration: IntegrationDefinition): boolean {
  return integration.supported;
}

function openDetails(integration: IntegrationDefinition) {
  if (!isOpenable(integration)) return;

  router.push({ name: AppRouteNames.INVESTOR_INTEGRATION_DETAILS, params: { integrationId: integration.id } });
}
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.page-header {
  margin-bottom: var(--t-spacing-lg);
}

.category-rail {
  width: 16rem;
  flex: 0 0 auto;
}

.results {
  flex: 1;
  min-width: 0;
}

.rail-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--t-spacing-sm);
  width: 100%;
  padding: var(--t-spacing-sm);
  border: 0;
  border-radius: var(--t-border-radius-sm);
  background: transparent;
  cursor: pointer;
  text-align: start;
  transition: background-color 150ms ease;

  &:hover {
    background-color: var(--t-color-neutral-50);
  }

  &.active {
    background-color: var(--t-color-neutral-100);
  }
}

.rail-divider {
  margin: var(--t-spacing-xs) 0;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--t-spacing-md);
}

.integration-card.openable {
  cursor: pointer;
}

.card-footer {
  margin-top: var(--t-spacing-xs);
  min-height: 2rem;
}

// Every card says what you can do with it in the same spot, button or not.
.card-action {
  flex: 0 0 auto;
  min-height: 2rem;
}

.footer-meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// Content-driven rather than a shared breakpoint: three-up, the cards drop under ~300px here.
@media (max-width: 1100px) {
  .card-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@include screen-max-md {
  .layout {
    flex-direction: column;
  }

  // Stacked, the row's align-start shrinks each column to its content, so both must claim the width.
  .category-rail,
  .results {
    width: 100%;
  }

  // Stacked, the rail would cost a third of the screen before the first integration; as a scrolling
  // strip of chips it costs one line and keeps every filter reachable.
  .rail-items {
    flex-direction: row;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .rail-items::-webkit-scrollbar {
    display: none;
  }

  .rail-item {
    width: auto;
    flex: 0 0 auto;
    white-space: nowrap;
  }

  // Doubled to outweigh the component's own horizontal-rule styling regardless of bundle order.
  .rail-divider.rail-divider {
    width: 1px;
    height: auto;
    flex: 0 0 auto;
    margin: 0;
  }
}

// Two columns still read fine on a large phone in landscape; below that a card gets too narrow.
@include screen-max-sm {
  .card-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
